import React, { useState, useEffect, useRef } from 'react';
import Toast from 'antd-mobile/es/components/toast';
import { VariableSizeList as List } from 'react-window';
import Cookie from 'js-cookie';
import './index.scss';
import MultiVehicleApi, {
  StationVehicleItem
} from '@/fetch/business/multiVehicle';
import { HttpStatusCode } from '@/fetch/core/constant';
import { sgmLog } from '@/utils';
import VehicleCard, { VehicleStatusEnum } from '../VehicleCard';
import LoadingDots from '@/components/LoadingDots';

// Chrome 52 兼容性处理
const requestAnimationFrame =
  window.requestAnimationFrame ||
  // @ts-ignore
  window.webkitRequestAnimationFrame ||
  function (callback: Function): number {
    return window.setTimeout(callback, 1000 / 60);
  };

const CSAT_CODE = 'RJXT';
const CSAT_POLL_INTERVAL = 200;
const CSAT_SGM_TYPE = 'WORKBENCH_CSAT';
let hasDispatchedCSAT = false;

// 定义数据获取方式的枚举
enum FetchType {
  INITIAL = 'INITIAL', // 初始加载
  MANUAL = 'MANUAL', // 手动刷新
  AUTO = 'AUTO' // 自动轮询
}

// 虚拟列表引用类型
interface ListRef {
  scrollTo: (offset: number) => void;
  scrollOffset?: number;
  resetAfterIndex: (index: number) => void;
  scrollToItem?: (index: number, align: string) => void;
  state?: {
    scrollOffset?: number;
  };
  [key: string]: any;
}

interface StationVehicleExtendFields {
  extra2: string;
  extra3: string;
}

/**
 * 车辆列表组件
 */
interface VehicleListProps {
  title?: string;
  fetchVehicleList?: () => Promise<any>;
  onVehicleClick?: (vehicle: any) => void;
  hideMileage?: boolean;
  enableCsat?: boolean;
}

const VehicleList = function ({
  title = '接驳车辆',
  fetchVehicleList,
  onVehicleClick,
  hideMileage = false,
  enableCsat = true
}: VehicleListProps) {
  const [vehicleList, setVehicleList] = useState<any[]>([]);
  const [dataLoadingState, setDataLoadingState] = useState<
    'init' | 'loading' | 'loaded'
  >('init');
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);

  const vehicleApiRef = useRef<MultiVehicleApi>(new MultiVehicleApi());
  const virtualListRef = useRef<ListRef | null>(null);
  const scrollPositionRef = useRef<number>(0);
  const virtualListHeight = window.innerHeight - 150;
  const baseItemHeight = 112;
  const csatPendingRef = useRef<boolean>(false);
  const csatPollTimerRef = useRef<number | null>(null);
  const enableCsatRef = useRef<boolean>(enableCsat);
  const stationVehicleExtendFieldsPromiseRef =
    useRef<Promise<StationVehicleExtendFields> | null>(null);
  const csatDispatchPromiseRef = useRef<Promise<boolean> | null>(null);

  const calculateItemHeight = function (index: number): number {
    const vehicle = vehicleList[index];
    if (!vehicle) return baseItemHeight;
    const extra1 =
      vehicle.vehicleStatus === VehicleStatusEnum.TASKING && !hideMileage
        ? 24
        : 0;
    const extra2 = vehicle.address ? 5 : 0;
    return extra1 + baseItemHeight + extra2;
  };

  const restoreScrollPosition = function (offset: number): void {
    if (
      virtualListRef.current &&
      typeof virtualListRef.current.scrollTo === 'function'
    ) {
      virtualListRef.current.scrollTo(offset);
    } else if (
      virtualListRef.current &&
      virtualListRef.current.scrollOffset !== undefined
    ) {
      virtualListRef.current.scrollOffset = offset;
    }
  };

  const setLoadingStateByFetchType = function (fetchType: FetchType): void {
    if (dataLoadingState === 'init' && fetchType === FetchType.INITIAL) {
      setDataLoadingState('loading');
    }
  };

  const handleSuccessResponse = function (response: any): void {
    setVehicleList(response.data || []);
    setDataLoadingState('loaded');

    requestAnimationFrame(function () {
      if (scrollPositionRef.current > 0) {
        restoreScrollPosition(scrollPositionRef.current);
      }
    });
  };

  const handleErrorResponse = function (response: any): void {
    if (response && response.message) {
      Toast.show({
        icon: 'fail',
        content: response.message || '获取车辆列表失败'
      });
    }

    setDataLoadingState('loaded');
  };

  const handleRequestError = function (error: any): void {
    setDataLoadingState('loaded');

    Toast.show({
      icon: 'fail',
      content: '获取车辆列表失败'
    });

    console.error('获取车辆列表失败', error);
  };

  const finalizeRequest = function (fetchType: FetchType): void {
    if (fetchType === FetchType.MANUAL) {
      setIsRefreshing(false);
    }
  };

  const fetchVehicleData = function (
    fetchType: FetchType = FetchType.AUTO
  ): Promise<void> {
    setLoadingStateByFetchType(fetchType);

    const fetchListRequest =
      fetchVehicleList || (() => vehicleApiRef.current.getVehicleList());

    return fetchListRequest()
      .then(function (response: any) {
        if (response && response.code === HttpStatusCode.Success) {
          handleSuccessResponse(response);
        } else {
          handleErrorResponse(response);
        }
      })
      .catch(function (error) {
        handleRequestError(error);
      })
      .finally(function () {
        finalizeRequest(fetchType);
        return undefined;
      });
  };

  const VehicleListRow = function (props: {
    index: number;
    style: React.CSSProperties;
  }): React.ReactNode {
    const rowIndex = props.index;
    const rowStyle = props.style;
    const vehicleItem = vehicleList[rowIndex];
    if (!vehicleItem) return null;

    const rowDivStyle = {
      height: `${calculateItemHeight(rowIndex)}px`,
      paddingBottom: '0px',
      overflow: 'hidden',
      width: '100%',
      boxSizing: 'border-box',
      marginBottom: props.index === vehicleList.length - 1 ? 0 : '5px',
      position: rowStyle.position,
      left: rowStyle.left,
      top: rowStyle.top,
      right: rowStyle.right,
      bottom: rowStyle.bottom
    };

    return (
      <div style={rowDivStyle as React.CSSProperties}>
        <VehicleCard
          vehicle={vehicleItem as any}
          onCardClick={onVehicleClick}
          hideMileage={hideMileage}
        />
      </div>
    );
  };

  useEffect(function () {
    fetchVehicleData(FetchType.INITIAL);
  }, []);

  const prevVehiclesCountRef = useRef<number>(0);
  const prevTaskingCountRef = useRef<number>(0);

  const clearCsatPoll = function () {
    if (csatPollTimerRef.current !== null) {
      window.clearInterval(csatPollTimerRef.current);
      csatPollTimerRef.current = null;
    }
  };

  const buildStationVehicleExtendFields = function (
    stationVehicleList: StationVehicleItem[]
  ): StationVehicleExtendFields {
    const vehicleTypeNames: string[] = [];
    const vehicleNames: string[] = [];

    stationVehicleList.forEach(function (item) {
      if (
        item.vehicleTypeName &&
        vehicleTypeNames.indexOf(item.vehicleTypeName) === -1
      ) {
        vehicleTypeNames.push(item.vehicleTypeName);
      }

      if (item.vehicleName) {
        vehicleNames.push(item.vehicleName);
      }
    });

    return {
      extra2: vehicleNames.join(','),
      extra3: vehicleTypeNames.join(',')
    };
  };

  const getStationVehicleExtendFields =
    function (): Promise<StationVehicleExtendFields> {
      if (stationVehicleExtendFieldsPromiseRef.current) {
        return stationVehicleExtendFieldsPromiseRef.current;
      }

      const siteId = Cookie.get('siteId');
      if (!siteId) {
        stationVehicleExtendFieldsPromiseRef.current = Promise.resolve({
          extra2: '',
          extra3: ''
        });
        return stationVehicleExtendFieldsPromiseRef.current;
      }

      stationVehicleExtendFieldsPromiseRef.current = vehicleApiRef.current
        .getStationVehicleList(siteId)
        .then(function (response) {
          if (
            response &&
            response.code === HttpStatusCode.Success &&
            Array.isArray(response.data)
          ) {
            return buildStationVehicleExtendFields(response.data);
          }

          return {
            extra2: '',
            extra3: ''
          };
        })
        .catch(function (error) {
          sgmLog(
            CSAT_SGM_TYPE,
            'GET_STATION_VEHICLES_ERROR',
            String(error || '')
          );
          console.error('获取站点车辆列表失败', error);
          return {
            extra2: '',
            extra3: ''
          };
        });

      return stationVehicleExtendFieldsPromiseRef.current;
    };

  const dispatchCsatWithExtendFields = function (): Promise<boolean> {
    if (!enableCsatRef.current) {
      return Promise.resolve(false);
    }

    const csat = (window as any).__JD_CSAT;
    if (!csat || typeof csat.dispatchCode !== 'function') {
      return Promise.resolve(false);
    }

    return getStationVehicleExtendFields().then(function (extendFields) {
      if (!enableCsatRef.current) {
        return false;
      }

      if (typeof csat.setExtendFields === 'function') {
        try {
          csat.setExtendFields({
            extra2: extendFields.extra2,
            extra3: extendFields.extra3,
            extra4: Cookie.get('siteName') || ''
          });
        } catch (error) {
          sgmLog(CSAT_SGM_TYPE, 'SET_EXTEND_FIELDS_ERROR', String(error || ''));
          console.error('设置CSAT扩展字段失败', error);
        }
      }

      try {
        csat.dispatchCode(CSAT_CODE);
        hasDispatchedCSAT = true;
        return true;
      } catch (error) {
        sgmLog(CSAT_SGM_TYPE, 'DISPATCH_CODE_ERROR', String(error || ''));
        console.error('派发CSAT失败', error);
        return false;
      }
    });
  };

  const tryDispatchCsat = function (): Promise<boolean> {
    if (!enableCsatRef.current) {
      return Promise.resolve(false);
    }

    if (hasDispatchedCSAT) {
      return Promise.resolve(true);
    }

    if (csatDispatchPromiseRef.current) {
      return csatDispatchPromiseRef.current;
    }

    const dispatchPromise = dispatchCsatWithExtendFields().then(
      function (isDispatched) {
        csatDispatchPromiseRef.current = null;
        return isDispatched;
      },
      function (error) {
        csatDispatchPromiseRef.current = null;
        sgmLog(CSAT_SGM_TYPE, 'DISPATCH_FLOW_ERROR', String(error || ''));
        console.error('执行CSAT派发流程失败', error);
        return false;
      }
    );

    csatDispatchPromiseRef.current = dispatchPromise;
    return dispatchPromise;
  };

  const startCsatPoll = function () {
    if (csatPollTimerRef.current !== null) return;
    csatPollTimerRef.current = window.setInterval(function () {
      tryDispatchCsat().then(function (isDispatched) {
        if (isDispatched) {
          csatPendingRef.current = false;
          clearCsatPoll();
        }
      });
    }, CSAT_POLL_INTERVAL);
  };

  useEffect(
    function () {
      enableCsatRef.current = enableCsat;
    },
    [enableCsat]
  );

  useEffect(function () {
    return function () {
      enableCsatRef.current = false;
      clearCsatPoll();
    };
  }, []);

  useEffect(
    function () {
      if (!enableCsat) {
        clearCsatPoll();
        csatPendingRef.current = false;
      }

      if (enableCsat && vehicleList.length > 0) {
        if (!hasDispatchedCSAT && !csatPendingRef.current) {
          csatPendingRef.current = true;
          tryDispatchCsat().then(function (isDispatched) {
            if (isDispatched) {
              csatPendingRef.current = false;
            } else if (enableCsatRef.current) {
              startCsatPoll();
            } else {
              csatPendingRef.current = false;
            }
          });
        }

        if (virtualListRef.current) {
          const currentTaskingCount = vehicleList.filter(
            (vehicleItem: any) =>
              vehicleItem.vehicleStatus === VehicleStatusEnum.TASKING
          ).length;

          if (
            prevVehiclesCountRef.current !== vehicleList.length ||
            prevTaskingCountRef.current !== currentTaskingCount
          ) {
            virtualListRef.current.resetAfterIndex(0);
            prevVehiclesCountRef.current = vehicleList.length;
            prevTaskingCountRef.current = currentTaskingCount;
          }
        }
      }
    },
    [enableCsat, vehicleList]
  );

  const handleVehicleRefresh = function () {
    if (isRefreshing) return;
    setIsRefreshing(true);
    fetchVehicleData(FetchType.MANUAL);
  };

  const getRefreshButtonClass = function () {
    return isRefreshing ? 'refresh-btn refreshing' : 'refresh-btn';
  };

  const renderRefreshButtonContent = function () {
    if (isRefreshing) {
      return <LoadingDots />;
    } else {
      return <React.Fragment>刷新车辆</React.Fragment>;
    }
  };

  const renderVehicleListContent = function () {
    if (dataLoadingState === 'loading') {
      return (
        <div className="loading-initial">
          <LoadingDots />
        </div>
      );
    } else if (vehicleList.length === 0 && dataLoadingState === 'loaded') {
      return (
        <div className="no-data">
          <span>暂无数据</span>
        </div>
      );
    } else {
      return (
        <List
          // @ts-ignore
          ref={virtualListRef}
          className="virtual-list"
          height={virtualListHeight}
          itemCount={vehicleList.length}
          itemSize={calculateItemHeight}
          width="100%"
          // @ts-ignore
          overscanCount={5}
        >
          {VehicleListRow}
        </List>
      );
    }
  };

  return (
    <div className="vehicle-page">
      <div className={`section-header `}>
        <div className="section-title">
          <div className="vertical-line"></div>
          <span>{title}</span>
        </div>
        <div
          className={getRefreshButtonClass()}
          clstag="transport_workbench|click-refresh"
          onClick={handleVehicleRefresh}
        >
          {renderRefreshButtonContent()}
        </div>
      </div>
      <div className="vehicle-list-container">{renderVehicleListContent()}</div>
    </div>
  );
};

export default VehicleList;
